/**
 * Money — importes monetarios exactos.
 *
 * REGLA INVIOLABLE (ADR-001): en este ERP no se representa dinero con
 * `number`. Este es un sistema que emite facturas con relevancia fiscal;
 * `0.1 + 0.2 !== 0.3` no es una curiosidad académica, es una diferencia
 * en el modelo 303.
 *
 * Implementación: enteros con signo (`bigint`) en unidades menores, con
 * escala explícita. Sin dependencias externas para poder ejecutarse en
 * React Native.
 *
 * Redondeo: HALF_UP (medio hacia arriba, alejándose del cero), que es el
 * criterio habitual en la facturación española.
 */

export type Currency = string; // ISO 4217, p.ej. 'EUR'

const DEFAULT_CURRENCY: Currency = 'EUR';

/** Escala por defecto de un importe (2 decimales). */
export const AMOUNT_SCALE = 2;
/** Escala de precios unitarios y tarifas (6 decimales, ver DATABASE.md §1). */
export const UNIT_PRICE_SCALE = 6;

const DECIMAL_RE = /^[+-]?\d+(\.\d+)?$/;

function pow10(n: number): bigint {
  let r = 1n;
  for (let i = 0; i < n; i++) r *= 10n;
  return r;
}

/** Divide redondeando HALF_UP respecto al cero. `den` debe ser > 0. */
function divRoundHalfUp(num: bigint, den: bigint): bigint {
  if (den <= 0n) throw new Error('El divisor debe ser positivo');
  const negative = num < 0n;
  const abs = negative ? -num : num;
  const q = abs / den;
  const r = abs * 2n >= den * (q * 2n + 1n) ? q + 1n : q;
  return negative ? -r : r;
}

/** Descompone una cadena decimal en (mantisa entera, escala). */
function parseDecimal(value: string): { mantissa: bigint; scale: number } {
  const trimmed = value.trim();
  if (!DECIMAL_RE.test(trimmed)) {
    throw new MoneyError(`Valor decimal inválido: "${value}"`);
  }
  const negative = trimmed.startsWith('-');
  const unsigned = trimmed.replace(/^[+-]/, '');
  const dot = unsigned.indexOf('.');
  if (dot === -1) {
    const m = BigInt(unsigned);
    return { mantissa: negative ? -m : m, scale: 0 };
  }
  const intPart = unsigned.slice(0, dot);
  const fracPart = unsigned.slice(dot + 1);
  const m = BigInt(`${intPart}${fracPart}`);
  return { mantissa: negative ? -m : m, scale: fracPart.length };
}

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

export interface MoneyJSON {
  /** Importe como cadena decimal con la escala fijada. Nunca un número. */
  amount: string;
  currency: Currency;
}

export class Money {
  private constructor(
    /** Importe en unidades menores según `scale`. */
    readonly minor: bigint,
    readonly scale: number,
    readonly currency: Currency,
  ) {
    if (!Number.isInteger(scale) || scale < 0 || scale > 12) {
      throw new MoneyError(`Escala inválida: ${scale}`);
    }
    if (!/^[A-Z]{3}$/.test(currency)) {
      throw new MoneyError(`Divisa inválida: "${currency}" (se espera ISO 4217)`);
    }
  }

  // ── Construcción ──────────────────────────────────────────────────

  /** Money.of('1234.56') · Money.of('-19.99', 'EUR') */
  static of(value: string, currency: Currency = DEFAULT_CURRENCY, scale = AMOUNT_SCALE): Money {
    const { mantissa, scale: valueScale } = parseDecimal(value);
    return new Money(mantissa, valueScale, currency).rescale(scale);
  }

  /** Construye desde unidades menores ya calculadas (p.ej. desde la BD). */
  static fromMinor(minor: bigint, scale = AMOUNT_SCALE, currency: Currency = DEFAULT_CURRENCY): Money {
    return new Money(minor, scale, currency);
  }

  static zero(currency: Currency = DEFAULT_CURRENCY, scale = AMOUNT_SCALE): Money {
    return new Money(0n, scale, currency);
  }

  /**
   * Rehidrata desde el formato de la API: `{ amount: '12.34', currency: 'EUR' }`.
   * La escala se deduce de los decimales presentes, con mínimo `AMOUNT_SCALE`.
   */
  static fromJSON(json: MoneyJSON): Money {
    const { scale } = parseDecimal(json.amount);
    return Money.of(json.amount, json.currency, Math.max(scale, AMOUNT_SCALE));
  }

  // ── Conversión ────────────────────────────────────────────────────

  /** Cambia la escala redondeando HALF_UP si se pierde precisión. */
  rescale(newScale: number): Money {
    if (newScale === this.scale) return this;
    if (newScale > this.scale) {
      return new Money(this.minor * pow10(newScale - this.scale), newScale, this.currency);
    }
    return new Money(divRoundHalfUp(this.minor, pow10(this.scale - newScale)), newScale, this.currency);
  }

  toString(): string {
    const negative = this.minor < 0n;
    const abs = (negative ? -this.minor : this.minor).toString().padStart(this.scale + 1, '0');
    const int = abs.slice(0, abs.length - this.scale) || '0';
    const frac = this.scale > 0 ? `.${abs.slice(abs.length - this.scale)}` : '';
    return `${negative ? '-' : ''}${int}${frac}`;
  }

  toJSON(): MoneyJSON {
    return { amount: this.toString(), currency: this.currency };
  }

  /**
   * Sólo para presentación. NUNCA usar el resultado para calcular:
   * devuelve `number` y por tanto pierde exactitud.
   */
  toNumberUnsafe(): number {
    return Number(this.toString());
  }

  // ── Aritmética ────────────────────────────────────────────────────

  private assertSameCurrency(other: Money): void {
    if (this.currency !== other.currency) {
      throw new MoneyError(
        `No se pueden operar importes de distinta divisa: ${this.currency} y ${other.currency}`,
      );
    }
  }

  /** Lleva ambos operandos a la escala mayor para no perder precisión. */
  private align(other: Money): [Money, Money] {
    const scale = Math.max(this.scale, other.scale);
    return [this.rescale(scale), other.rescale(scale)];
  }

  add(other: Money): Money {
    this.assertSameCurrency(other);
    const [a, b] = this.align(other);
    return new Money(a.minor + b.minor, a.scale, a.currency);
  }

  subtract(other: Money): Money {
    this.assertSameCurrency(other);
    const [a, b] = this.align(other);
    return new Money(a.minor - b.minor, a.scale, a.currency);
  }

  /**
   * Multiplica por una cantidad decimal exacta (unidades, horas, coeficientes).
   * El resultado conserva la escala del importe original.
   */
  multiply(factor: string | number | bigint, resultScale = this.scale): Money {
    const f = parseDecimal(typeof factor === 'string' ? factor : factor.toString());
    // (minor / 10^scale) * (mantissa / 10^fScale) → escalamos y redondeamos una sola vez
    const rawScale = this.scale + f.scale;
    const raw = this.minor * f.mantissa;
    return new Money(raw, rawScale, this.currency).rescale(resultScale);
  }

  /** Aplica un porcentaje: `money.percentage('21')` para el 21 % de IVA. */
  percentage(rate: string | number, resultScale = this.scale): Money {
    return this.multiply(rate, resultScale + 4).multiply('0.01', resultScale);
  }

  negate(): Money {
    return new Money(-this.minor, this.scale, this.currency);
  }

  abs(): Money {
    return this.minor < 0n ? this.negate() : this;
  }

  /**
   * Reparte el importe en `n` partes o según pesos, **sin perder ni inventar
   * céntimos**: el remanente se distribuye por el método del mayor resto,
   * de forma determinista.
   *
   * Necesario para prorratear una cuota de contrato entre centros o repartir
   * un descuento global entre líneas de factura.
   */
  allocate(weights: readonly number[]): Money[] {
    if (weights.length === 0) throw new MoneyError('Se requiere al menos un peso');
    if (weights.some((w) => w < 0)) throw new MoneyError('Los pesos no pueden ser negativos');

    const total = weights.reduce((a, b) => a + b, 0);
    if (total === 0) throw new MoneyError('La suma de pesos no puede ser cero');

    const totalBig = BigInt(Math.round(total * 1e6));
    const shares: bigint[] = [];
    let assigned = 0n;

    for (const w of weights) {
      const wBig = BigInt(Math.round(w * 1e6));
      const share = (this.minor * wBig) / totalBig; // truncado hacia cero
      shares.push(share);
      assigned += share;
    }

    // Distribuye el remanente de unidad en unidad, empezando por los mayores pesos.
    let remainder = this.minor - assigned;
    const step = remainder >= 0n ? 1n : -1n;
    const order = weights
      .map((w, i) => ({ w, i }))
      .sort((a, b) => b.w - a.w || a.i - b.i);

    let idx = 0;
    while (remainder !== 0n) {
      const target = order[idx % order.length];
      /* c8 ignore next */
      if (!target) break;
      shares[target.i] = (shares[target.i] ?? 0n) + step;
      remainder -= step;
      idx++;
    }

    return shares.map((s) => new Money(s, this.scale, this.currency));
  }

  // ── Comparación ───────────────────────────────────────────────────

  compare(other: Money): -1 | 0 | 1 {
    this.assertSameCurrency(other);
    const [a, b] = this.align(other);
    if (a.minor < b.minor) return -1;
    if (a.minor > b.minor) return 1;
    return 0;
  }

  equals(other: Money): boolean {
    return this.currency === other.currency && this.compare(other) === 0;
  }

  isZero(): boolean {
    return this.minor === 0n;
  }

  isNegative(): boolean {
    return this.minor < 0n;
  }

  isPositive(): boolean {
    return this.minor > 0n;
  }

  // ── Agregación ────────────────────────────────────────────────────

  static sum(values: readonly Money[], currency: Currency = DEFAULT_CURRENCY): Money {
    if (values.length === 0) return Money.zero(currency);
    return values.reduce((acc, v) => acc.add(v));
  }
}
